import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { createPortal } from "react-dom";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Date formatting without timezone issues
const formatDateWithLocale = (
  dateValue,
  locale = "en-GB",
  format = "DD-MM-YYYY"
) => {
  if (!dateValue) return "";

  // Handle string dates (YYYY-MM-DD format)
  if (typeof dateValue === "string" && dateValue.includes("-")) {
    const [year, month, day = "01"] = dateValue.split("-");
    if (year && month) {
      const dayStr = String(parseInt(day)).padStart(2, "0");
      const monthStr = String(parseInt(month)).padStart(2, "0");
      const yearStr = String(parseInt(year));

      switch (format) {
        case "DD-MM-YYYY":
          return `${dayStr}-${monthStr}-${yearStr}`;
        case "MM-DD-YYYY":
          return `${monthStr}-${dayStr}-${yearStr}`;
        case "YYYY-MM-DD":
          return `${yearStr}-${monthStr}-${dayStr}`;
        default:
          return `${dayStr}-${monthStr}-${yearStr}`;
      }
    }
  }

  // Handle Date objects
  if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
    const day = String(dateValue.getDate()).padStart(2, "0");
    const month = String(dateValue.getMonth() + 1).padStart(2, "0");
    const year = dateValue.getFullYear();

    switch (format) {
      case "DD-MM-YYYY":
        return `${day}-${month}-${year}`;
      case "MM-DD-YYYY":
        return `${month}-${day}-${year}`;
      case "YYYY-MM-DD":
        return `${year}-${month}-${day}`;
      default:
        return `${day}-${month}-${year}`;
    }
  }

  return "";
};

// Parse string dates to Date objects for display
const parseDateFromString = (
  dateValue,
  locale = "en-GB",
  format = "DD-MM-YYYY"
) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date)
    return isNaN(dateValue.getTime()) ? null : dateValue;

  if (typeof dateValue === "string") {
    // Handle ISO format (YYYY-MM-DD)
    if (dateValue.includes("-") && dateValue.length >= 8) {
      const parts = dateValue.split("-");
      if (parts.length === 3) {
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const day = parseInt(parts[2] || "1");

        // If it looks like YYYY-MM-DD format
        if (
          parts[0].length === 4 &&
          year > 1900 &&
          month >= 1 &&
          month <= 12 &&
          day >= 1 &&
          day <= 31
        ) {
          return new Date(year, month - 1, day);
        }

        // Handle DD-MM-YYYY or MM-DD-YYYY format
        if (parts[0].length <= 2) {
          const [first, second, third] = parts;
          let date;
          if (format === "DD-MM-YYYY") {
            date = new Date(
              parseInt(third),
              parseInt(second) - 1,
              parseInt(first)
            );
          } else if (format === "MM-DD-YYYY") {
            date = new Date(
              parseInt(third),
              parseInt(first) - 1,
              parseInt(second)
            );
          }
          if (!isNaN(date.getTime())) return date;
        }
      }
    }
  }

  return null;
};

// Parse manual typed date input (e.g., "02022025" -> "2025-02-02")
const parseManualInput = (input, format = "DD-MM-YYYY") => {
  if (!input || typeof input !== "string") return null;

  const numericOnly = input.replace(/[^0-9]/g, "");

  if (numericOnly.length >= 6) {
    let day, month, year;

    switch (format) {
      case "DD-MM-YYYY":
        day = numericOnly.substring(0, 2);
        month = numericOnly.substring(2, 4);
        year = numericOnly.substring(4, 8) || "2025";
        break;
      case "MM-DD-YYYY":
        month = numericOnly.substring(0, 2);
        day = numericOnly.substring(2, 4);
        year = numericOnly.substring(4, 8) || "2025";
        break;
      case "YYYY-MM-DD":
        year = numericOnly.substring(0, 4);
        month = numericOnly.substring(4, 6);
        day = numericOnly.substring(6, 8) || "01";
        break;
      default:
        day = numericOnly.substring(0, 2);
        month = numericOnly.substring(2, 4);
        year = numericOnly.substring(4, 8) || "2025";
    }

    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (
      dayNum >= 1 &&
      dayNum <= 31 &&
      monthNum >= 1 &&
      monthNum <= 12 &&
      yearNum >= 1900 &&
      yearNum <= 2100
    ) {
      const test = new Date(yearNum, monthNum - 1, dayNum);
      if (
        test.getFullYear() === yearNum &&
        test.getMonth() === monthNum - 1 &&
        test.getDate() === dayNum
      ) {
        return `${yearNum}-${String(monthNum).padStart(2, "0")}-${String(
          dayNum
        ).padStart(2, "0")}`;
      }
    }
  }

  return null;
};

// Format a progressive display while typing
const formatDisplayValue = (value, isTyping, format = "DD-MM-YYYY") => {
  if (!value) return "";

  if (isTyping && typeof value === "string" && !value.includes("-")) {
    const numericOnly = value.replace(/[^0-9]/g, "");

    if (numericOnly.length <= 2) {
      return numericOnly;
    } else if (numericOnly.length <= 4) {
      const part1 = numericOnly.substring(0, 2);
      const part2 = numericOnly.substring(2);
      return `${part1}-${part2}`;
    } else {
      const part1 = numericOnly.substring(0, 2);
      const part2 = numericOnly.substring(2, 4);
      const part3 = numericOnly.substring(4);
      return `${part1}-${part2}-${part3}`;
    }
  }

  return formatDateWithLocale(value, "en-GB", format);
};

// Normalize to date-only for min/max comparison (YYYY-MM-DD string -> Date at midnight)
const toDateOnly = (val) => {
  if (!val) return null;
  if (val instanceof Date) return new Date(val.getFullYear(), val.getMonth(), val.getDate());
  if (typeof val === "string" && val.includes("-")) {
    const [y, m, d] = val.split("-").map(Number);
    if (y && m) return new Date(y, (m || 1) - 1, d || 1);
  }
  return null;
};

// Ultra Compact Date Grid Component
const DateGrid = React.memo(
  ({ currentDate, onSelect, selectedDate, disablePast, disableFuture, minDate, maxDate }) => {
    const generateCalendarDates = useMemo(() => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );
      const minD = toDateOnly(minDate);
      const maxD = toDateOnly(maxDate);

      const dates = [];
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());

      for (let i = 0; i < 42; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        const dayStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const isCurrentMonth = currentDate.getMonth() === month;
        const isPast = currentDate < todayStart;
        const isFuture = currentDate > todayStart;
        const beforeMin = minD && dayStart < minD;
        const afterMax = maxD && dayStart > maxD;

        dates.push({
          date: currentDate.getDate(),
          isCurrentMonth,
          isPast: (isPast && disablePast) || beforeMin,
          isFuture: (isFuture && disableFuture) || afterMax,
          fullDate: currentDate,
        });
      }
      return dates;
    }, [currentDate, disablePast, disableFuture, minDate, maxDate]);

    const isDateSelected = (dateObj) => {
      if (!selectedDate || !dateObj) return false;
      return dateObj.fullDate.toDateString() === selectedDate.toDateString();
    };

    const isToday = (dateObj) =>
      dateObj.fullDate.toDateString() === new Date().toDateString();

    return (
      <div className="h-full flex flex-col">
        <div className="grid grid-cols-7 gap-1 mb-1" style={{ height: 12 }}>
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
            (day, index) => (
              <div
                key={index}
                className="text-center text-[9px] font-medium text-gray-600 flex items-center justify-center"
              >
                {day.slice(0, 1)}
              </div>
            )
          )}
        </div>

        <div className="grid grid-cols-7 gap-1 flex-1">
          {generateCalendarDates.map((dateObj, index) => (
            <button
              key={index}
              onClick={() => onSelect(dateObj)}
              disabled={dateObj.isPast || dateObj.isFuture}
              className={`
                w-full rounded text-[9px] font-medium flex items-center justify-center
                ${dateObj.isCurrentMonth ? "text-gray-900" : "text-gray-400"}
                ${
                  isDateSelected(dateObj)
                    ? "bg-blue-600 text-white shadow-sm"
                    : isToday(dateObj)
                    ? "bg-blue-100 text-blue-700 border border-blue-600"
                    : dateObj.isCurrentMonth
                    ? "hover:bg-gray-100"
                    : "hover:bg-gray-50"
                }
                ${
                  dateObj.isPast || dateObj.isFuture
                    ? "text-gray-300 cursor-not-allowed"
                    : "cursor-pointer"
                }
              `}
              style={{ height: "16px" }}
              aria-label={`Select ${dateObj.fullDate.toLocaleDateString()}`}
            >
              {dateObj.date}
            </button>
          ))}
        </div>
      </div>
    );
  }
);

// Month Selector Component
const MonthSelector = React.memo(({ currentDate, onSelect }) => (
  <div className="grid grid-cols-4 gap-1 h-full">
    {[
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ].map((month, index) => (
      <button
        key={index}
        onClick={() => onSelect(index)}
        className={`
          rounded text-[9px] font-medium flex items-center justify-center
          ${
            currentDate.getMonth() === index
              ? "bg-blue-600 text-white shadow-sm"
              : "text-gray-700 hover:bg-gray-100"
          }
        `}
        style={{ height: "28px" }}
      >
        {month}
      </button>
    ))}
  </div>
));

// Year Selector Component
const YearSelector = React.memo(
  ({ currentDate, yearPage, setYearPage, onSelect }) => {
    const years = useMemo(() => {
      const currentYear = currentDate.getFullYear();
      const baseYear = currentYear - 6;
      const startYear = baseYear + yearPage * 12;
      return Array.from({ length: 12 }, (_, i) => startYear + i);
    }, [yearPage, currentDate]);

    return (
      <div className="h-full flex flex-col">
        <div
          className="flex items-center justify-between mb-1"
          style={{ height: 20 }}
        >
          <button
            onClick={() => setYearPage((prev) => prev - 1)}
            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-[9px] font-medium"
            disabled={yearPage <= -50}
          >
            ←
          </button>
          <span className="text-[9px] font-semibold text-gray-700">
            {years[0]}-{years[11]}
          </span>
          <button
            onClick={() => setYearPage((prev) => prev + 1)}
            className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-[9px] font-medium"
            disabled={yearPage >= 50}
          >
            →
          </button>
        </div>
        <div className="grid grid-cols-4 gap-1 flex-1">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => onSelect(year)}
              className={`
                rounded text-[9px] font-medium flex items-center justify-center
                ${
                  currentDate.getFullYear() === year
                    ? "bg-blue-600 text-white shadow-sm"
                    : year === new Date().getFullYear()
                    ? "bg-blue-100 text-blue-700 border border-blue-600"
                    : "text-gray-700 hover:bg-gray-100"
                }
              `}
              style={{ height: "22px" }}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
    );
  }
);

// Custom hook to sync currentDate with value
const useDateSync = (value, locale, dateFormat, isTyping, setCurrentDate) => {
  useEffect(() => {
    if (!isTyping) {
      const parsedDate = parseDateFromString(value, locale, dateFormat);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        setCurrentDate(parsedDate);
      }
    }
  }, [value, locale, dateFormat, isTyping, setCurrentDate]);
};

// FixedSizeCalendar Component
const FixedSizeCalendar = ({
  onChange,
  value,
  currentDate,
  setCurrentDate,
  disabled,
  locale,
  dateFormat,
  disablePast,
  disableFuture,
  minDate,
  maxDate,
  setIsOpen,
  setView,
  setIsFocused,
  setIsTyping,
  setTypedValue,
  inputRef,
  position,
  portalRef,
  yearPage,
  setYearPage,
  view,
}) => {
  const displayDate = parseDateFromString(value, locale, dateFormat);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateSelect = (dateObj) => {
    if (!dateObj || dateObj.isPast || dateObj.isFuture || disabled) return;

    const selectedDate = dateObj.fullDate;
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");

    const dateString = `${year}-${month}-${day}`;

    onChange(dateString);
    setCurrentDate(selectedDate);
    setIsOpen(false);
    setView("date");
    setIsFocused(false);
    setIsTyping(false);
    setTypedValue("");
    inputRef.current?.blur();
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(monthIndex);
    setCurrentDate(newDate);
    setView("date");
  };

  const handleYearSelect = (year) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(year);
    setCurrentDate(newDate);
    setView("month");
  };

  if (!position.width) return null;

  return (
    <div
      ref={portalRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
        zIndex: 99999,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      }}
      role="dialog"
      aria-label="Date picker"
    >
      {!position.isConstrained && position.placement === "bottom" && (
        <div
          className="absolute"
          style={{
            top: -6,
            left: 16,
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: "6px solid white",
            filter: "drop-shadow(0 -2px 3px rgba(0,0,0,0.1))",
          }}
        />
      )}

      <div
        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1"
        style={{ height: 30 }}
      >
        <div className="flex items-center justify-between h-full">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
            type="button"
          >
            <ChevronLeftIcon className="w-3 h-3" />
          </button>

          <div className="text-center flex-1">
            <div className="flex items-center justify-center space-x-2">
              <button
                onClick={() => setView("month")}
                className="text-[11px] font-medium opacity-90 hover:opacity-100 px-1 rounded"
                type="button"
              >
                {currentDate.toLocaleString(locale, { month: "short" })}
              </button>
              <button
                onClick={() => setView("year")}
                className="text-[11px] font-semibold hover:bg-white hover:bg-opacity-20 px-1 rounded"
                type="button"
              >
                {currentDate.getFullYear()}
              </button>
            </div>
          </div>

          <button
            onClick={() => navigateMonth(1)}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full"
            type="button"
          >
            <ChevronRightIcon className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div className="p-2" style={{ height: 160, overflow: "hidden" }}>
        {view === "date" && (
          <div className="h-full">
            <DateGrid
              currentDate={currentDate}
              onSelect={handleDateSelect}
              selectedDate={displayDate}
              disablePast={disablePast}
              disableFuture={disableFuture}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        )}
        {view === "month" && (
          <div className="h-full">
            <MonthSelector
              currentDate={currentDate}
              onSelect={handleMonthSelect}
            />
          </div>
        )}
        {view === "year" && (
          <div className="h-full">
            <YearSelector
              currentDate={currentDate}
              yearPage={yearPage}
              setYearPage={setYearPage}
              onSelect={handleYearSelect}
            />
          </div>
        )}
      </div>

      <div
        className="bg-gray-50 border-t border-gray-200 flex items-center justify-between px-2 py-1"
        style={{ height: 30 }}
      >
        <button
          onClick={() => {
            const today = new Date();
            setCurrentDate(today);
            setView("date");
            if (!disablePast && !disableFuture) {
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, "0");
              const day = String(today.getDate()).padStart(2, "0");
              const dateString = `${year}-${month}-${day}`;

              onChange(dateString);
              setIsOpen(false);
              setIsFocused(false);
              setIsTyping(false);
              setTypedValue("");
              inputRef.current?.blur();
            }
          }}
          className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-[10px] font-medium"
          type="button"
        >
          Today
        </button>

        <div className="flex items-center space-x-1">
          {onChange && value && (
            <button
              onClick={() => {
                onChange("");
                setIsOpen(false);
                setIsFocused(false);
                setIsTyping(false);
                setTypedValue("");
                inputRef.current?.blur();
              }}
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-[10px]"
              type="button"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => {
              setIsOpen(false);
              setIsFocused(false);
              setIsTyping(false);
              setTypedValue("");
              inputRef.current?.blur();
            }}
            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-[10px]"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const displayDate = parseDateFromString(
                value,
                locale,
                dateFormat
              );
              if (displayDate) {
                const year = displayDate.getFullYear();
                const month = String(displayDate.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const day = String(displayDate.getDate()).padStart(2, "0");
                const dateString = `${year}-${month}-${day}`;

                onChange(dateString);
              }
              setIsOpen(false);
              setIsFocused(false);
              setIsTyping(false);
              setTypedValue("");
              inputRef.current?.blur();
            }}
            className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-[10px] font-medium"
            type="button"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Inner component to handle Controller rendering
const DatePickerInner = ({
  name,
  onChange,
  value,
  fieldState,
  disabled,
  disablePast,
  disableFuture,
  minDate,
  maxDate,
  placeholder,
  error,
  className,
  fontSize,
  inputSize,
  inputBgColor,
  isFloatingLabel,
  leftIcon,
  rightIcon,
  iconBgColor,
  onLeftIconClick,
  onRightIconClick,
  locale,
  dateFormat,
  onClear,
  allowManualInput,
  onManualInputError,
  defaultValue,
  label,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("date");
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState({});
  const [yearPage, setYearPage] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [typedValue, setTypedValue] = useState("");

  const wrapperRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);

  // Sync currentDate with value
  useDateSync(value, locale, dateFormat, isTyping, setCurrentDate);

  // Initialize year page and sync currentDate with defaultValue
  useEffect(() => {
    const initializeDate = (initValue) => {
      const parsedDate = parseDateFromString(initValue, locale, dateFormat);
      const initialDate =
        parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : new Date();
      setCurrentDate(initialDate);
      const currentYear = initialDate.getFullYear();
      const baseYear = currentYear - 6;
      const initialPage = Math.floor((currentYear - baseYear) / 12);
      setYearPage(initialPage);
    };

    initializeDate(defaultValue || "");
  }, [defaultValue, locale, dateFormat]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsOpen(false);
      setView("date");
      setIsFocused(false);
      setIsTyping(false);
      setTypedValue("");
    };
  }, []);

  // Smart position calculation for calendar
  const calculateSmartPosition = useCallback(() => {
    if (!wrapperRef.current || !isOpen) return;

    const inputRect = wrapperRef.current.getBoundingClientRect();
    const scrollX = window.pageXOffset;
    const scrollY = window.pageYOffset;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const MODAL_WIDTH = 240;
    const MODAL_HEIGHT = 220;

    let constrainingContainer = null;
    let element = wrapperRef.current.parentElement;

    while (element && element !== document.body) {
      const computedStyle = window.getComputedStyle(element);
      const overflow = computedStyle.overflow;
      const overflowY = computedStyle.overflowY;
      const position = computedStyle.position;

      if (
        (element.hasAttribute("role") &&
          (element.getAttribute("role") === "dialog" ||
            element.getAttribute("role") === "modal")) ||
        element.classList.contains("modal") ||
        element.classList.contains("dialog") ||
        element.hasAttribute("aria-modal") ||
        position === "fixed" ||
        overflow === "hidden" ||
        overflow === "auto" ||
        overflow === "scroll" ||
        overflowY === "hidden" ||
        overflowY === "auto" ||
        overflowY === "scroll"
      ) {
        constrainingContainer = element;
        break;
      }
      element = element.parentElement;
    }

    let containerRect = {
      top: 0,
      left: 0,
      right: viewportWidth,
      bottom: viewportHeight,
      width: viewportWidth,
      height: viewportHeight,
    };
    if (constrainingContainer) {
      containerRect = constrainingContainer.getBoundingClientRect();
    }

    const spaceBelow = containerRect.bottom - inputRect.bottom;
    const spaceAbove = inputRect.top - containerRect.top;
    const spaceLeft = inputRect.left - containerRect.left;
    const spaceRight = containerRect.right - inputRect.right;

    let top,
      left,
      placement = "bottom";

    if (spaceBelow >= MODAL_HEIGHT + 8) {
      top = inputRect.bottom + scrollY + 4;
      placement = "bottom";
    } else if (spaceAbove >= MODAL_HEIGHT + 8) {
      top = inputRect.top + scrollY - MODAL_HEIGHT - 4;
      placement = "top";
    } else {
      if (spaceBelow >= spaceAbove) {
        top = Math.max(
          containerRect.top + scrollY + 8,
          inputRect.bottom + scrollY + 4
        );
        placement = "bottom";
      } else {
        top = Math.min(
          containerRect.bottom + scrollY - MODAL_HEIGHT - 8,
          inputRect.top + scrollY - MODAL_HEIGHT - 4
        );
        placement = "top";
      }
    }

    if (spaceRight >= MODAL_WIDTH) {
      left = inputRect.left + scrollX;
    } else if (spaceLeft >= MODAL_WIDTH) {
      left = inputRect.right + scrollX - MODAL_WIDTH;
    } else {
      left =
        containerRect.left + scrollX + (containerRect.width - MODAL_WIDTH) / 2;
    }

    left = Math.max(
      containerRect.left + scrollX + 8,
      Math.min(left, containerRect.right + scrollX - MODAL_WIDTH - 8)
    );

    top = Math.max(
      containerRect.top + scrollY + 8,
      Math.min(top, containerRect.bottom + scrollY - MODAL_HEIGHT - 8)
    );

    setPosition({
      top,
      left,
      placement,
      width: MODAL_WIDTH,
      height: MODAL_HEIGHT,
      isConstrained: !!constrainingContainer,
    });
  }, [isOpen]);

  // Handle outside clicks and window events
  useEffect(() => {
    if (isOpen) {
      calculateSmartPosition();

      const handleClick = (e) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target) &&
          portalRef.current &&
          !portalRef.current.contains(e.target)
        ) {
          setIsOpen(false);
          setView("date");
          setIsFocused(false);
          setIsTyping(false);
          setTypedValue("");
          inputRef.current?.blur();
        }
      };

      const handleResize = calculateSmartPosition;
      const handleScroll = calculateSmartPosition;

      document.addEventListener("mousedown", handleClick);
      window.addEventListener("resize", handleResize);
      document.addEventListener("scroll", handleScroll, true);

      return () => {
        document.removeEventListener("mousedown", handleClick);
        window.removeEventListener("resize", handleResize);
        document.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isOpen, calculateSmartPosition]);

  const getHeightClass = () => {
    const sizes = ["h-7", "h-8", "h-9", "h-10"];
    return sizes.includes(inputSize) ? inputSize : "h-8";
  };

  const getFontSizeClass = () => {
    const sizes = ["text-xs", "text-sm", "text-base"];
    return sizes.includes(fontSize) ? fontSize : "text-xs";
  };

  // Arrow up/down: change date by one day (for fromDate/toDate filters)
  const handleArrowKeyDown = useCallback(
    (e) => {
      if (disabled) return;
      const delta = e.key === "ArrowUp" ? 1 : e.key === "ArrowDown" ? -1 : null;
      if (delta === null) return;
      e.preventDefault();
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      let base = parseDateFromString(value, locale, dateFormat);
      if (!base || isNaN(base.getTime())) base = new Date(todayStart);
      const next = new Date(base.getFullYear(), base.getMonth(), base.getDate() + delta);
      const nextStart = new Date(next.getFullYear(), next.getMonth(), next.getDate());
      if (disablePast && nextStart < todayStart) return;
      if (disableFuture && nextStart > todayStart) return;
      const minD = toDateOnly(minDate);
      const maxD = toDateOnly(maxDate);
      if (minD && nextStart < minD) return;
      if (maxD && nextStart > maxD) return;
      const year = next.getFullYear();
      const month = String(next.getMonth() + 1).padStart(2, "0");
      const day = String(next.getDate()).padStart(2, "0");
      const dateString = `${year}-${month}-${day}`;
      onChange(dateString);
      setCurrentDate(nextStart);
    },
    [disabled, value, locale, dateFormat, disablePast, disableFuture, minDate, maxDate, onChange]
  );

  // Manual input handling
  // Manual input handling with past/future validation
  const handleManualInput = (nextVal, onChange) => {
    const cleanedValue = nextVal.replace(/[^0-9]/g, "");
    setIsTyping(true);
    setTypedValue(cleanedValue);

    if (!cleanedValue) {
      onChange("");
      setIsTyping(false);
      setTypedValue("");
      return;
    }

    const parsed = parseManualInput(cleanedValue, dateFormat);
    if (parsed) {
      // Parse the date to validate against past/future
      const inputDate = parseDateFromString(parsed, locale, dateFormat);
      const today = new Date();
      const todayStart = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate()
      );

      // Validate against disablePast
      if (disablePast && inputDate < todayStart) {
        if (onManualInputError) {
          onManualInputError("Past dates are not allowed");
        }
        setIsTyping(false);
        setTypedValue("");
        return;
      }

      // Validate against disableFuture
      if (disableFuture && inputDate > todayStart) {
        if (onManualInputError) {
          onManualInputError("Future dates are not allowed");
        }
        setIsTyping(false);
        setTypedValue("");
        return;
      }

      // Validate against minDate / maxDate
      const minD = toDateOnly(minDate);
      const maxD = toDateOnly(maxDate);
      if (minD && inputDate < minD) {
        if (onManualInputError) {
          onManualInputError("Date cannot be before the minimum date");
        }
        setIsTyping(false);
        setTypedValue("");
        return;
      }
      if (maxD && inputDate > maxD) {
        if (onManualInputError) {
          onManualInputError("Date cannot be after the maximum date");
        }
        setIsTyping(false);
        setTypedValue("");
        return;
      }

      // Date is valid, proceed
      onChange(parsed);
      if (inputDate) setCurrentDate(inputDate);
      setIsTyping(false);
      setTypedValue("");
    } else if (cleanedValue.length >= 8 && onManualInputError) {
      onManualInputError("Invalid date");
    }
  };

  const getInputDisplay = (value) =>
    isTyping && typedValue
      ? formatDisplayValue(typedValue, true, dateFormat)
      : formatDateWithLocale(value, locale, dateFormat);

  const hasError = Boolean(fieldState.error || error);
  const hasValue = Boolean(value);
  const shouldFloatLabel = isFloatingLabel && (isFocused || isOpen || hasValue);

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`
            ${
              isFloatingLabel
                ? `absolute pointer-events-none z-10 ${
                    shouldFloatLabel
                      ? "top-0 text-[10px] scale-75 origin-top-left bg-white px-0.5"
                      : "top-1/2 text-xs scale-100 origin-center"
                  }`
                : "block mb-1"
            } ${getFontSizeClass()} ${
            hasError
              ? "text-red-500"
              : shouldFloatLabel
              ? "text-blue-600"
              : "text-gray-500"
          }`}
          style={
            isFloatingLabel
              ? {
                  left: leftIcon
                    ? shouldFloatLabel
                      ? "40px"
                      : "40px"
                    : shouldFloatLabel
                    ? "10px"
                    : "10px",
                  transform: shouldFloatLabel
                    ? "translateY(-50%) scale(0.75)"
                    : "translateY(-50%) scale(1)",
                  transformOrigin: "left center",
                }
              : {}
          }
        >
          {label}
        </label>
      )}

      <div className="relative" ref={wrapperRef}>
        <div
          className={`relative flex items-stretch ${
            hasError
              ? "focus-within:ring-1 focus-within:ring-red-500"
              : "focus-within:ring-1 focus-within:ring-blue-500"
          } rounded-md`}
        >
          {leftIcon && (
            <div
              className={`
                flex items-center justify-center w-8 ${getHeightClass()} ${iconBgColor} border border-r-0 ${
                hasError ? "border-red-500" : "border-gray-300"
              } rounded-l-md ${
                onLeftIconClick ? "cursor-pointer hover:opacity-80" : ""
              } ${disabled ? "opacity-50" : ""} flex-shrink-0`}
              onClick={onLeftIconClick}
            >
              {React.cloneElement(leftIcon, {
                className: `w-3 h-3 flex-shrink-0 ${
                  leftIcon?.props?.className || "text-gray-500"
                }`,
              })}
            </div>
          )}

          <div
            className={`
              flex items-center flex-1 min-w-0 ${getHeightClass()} ${getFontSizeClass()} border ${inputBgColor} ${
              hasError ? "border-red-500" : "border-gray-300"
            } ${
              disabled
                ? "opacity-50 cursor-not-allowed"
                : allowManualInput
                ? ""
                : "cursor-pointer"
            } ${leftIcon ? "rounded-l-none border-l-0" : "rounded-l-md"} ${
              rightIcon ? "rounded-r-none border-r-0" : "rounded-r-md"
            } hover:border-gray-400 relative`}
          >
            {allowManualInput ? (
              <input
                id={name}
                ref={inputRef}
                type="text"
                value={getInputDisplay(value)}
                onChange={(e) => handleManualInput(e.target.value, onChange)}
                onKeyDown={handleArrowKeyDown}
                onFocus={() => {
                  setIsFocused(true);
                }}
                onBlur={() => {
                  setIsFocused(false);
                  setIsTyping(false);
                  setTypedValue("");
                }}
                disabled={disabled}
                placeholder={!shouldFloatLabel ? placeholder : ""}
                className={`flex-1 min-w-0 px-2 py-0.5 bg-transparent outline-none ${getFontSizeClass()}`}
                autoComplete="off"
                inputMode="numeric"
                aria-label="Type date"
                {...rest}
              />
            ) : (
              <div
                className="flex-1 min-w-0 overflow-hidden"
                onClick={() => {
                  if (!disabled) {
                    setIsOpen(!isOpen);
                    setIsFocused(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    handleArrowKeyDown(e);
                  } else if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!disabled) {
                      setIsOpen(!isOpen);
                      setIsFocused(true);
                    }
                  }
                }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-label="Open date picker"
                ref={inputRef}
              >
                <span
                  className={`block px-2 py-0.5 truncate ${
                    hasValue ? "text-gray-900" : "text-gray-400"
                  } ${getFontSizeClass()}`}
                >
                  {hasValue
                    ? formatDateWithLocale(value, locale, dateFormat)
                    : !shouldFloatLabel
                    ? placeholder
                    : ""}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1 pr-2 flex-shrink-0">
              {!disabled && (
                <>
                  {hasValue && onClear && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                        setIsTyping(false);
                        setTypedValue("");
                        onChange("");
                      }}
                      className="p-0.5 hover:bg-gray-100 rounded-full"
                      type="button"
                      aria-label="Clear date"
                    >
                      <XMarkIcon className="h-3 w-3 text-gray-500" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(!isOpen);
                      setIsFocused(true);
                    }}
                    className="p-0.5 hover:bg-gray-100 rounded-full"
                    type="button"
                    aria-label="Open calendar"
                  >
                    <CalendarDaysIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
                  </button>
                </>
              )}
            </div>
          </div>

          {rightIcon && (
            <div
              className={`
                flex items-center justify-center w-8 ${getHeightClass()} ${iconBgColor} border border-l-0 ${
                hasError ? "border-red-500" : "border-gray-300"
              } rounded-r-md ${
                onRightIconClick ? "cursor-pointer hover:opacity-80" : ""
              } ${disabled ? "opacity-50" : ""} flex-shrink-0`}
              onClick={onRightIconClick}
            >
              {React.cloneElement(rightIcon, {
                className: `w-3 h-3 flex-shrink-0 ${
                  rightIcon?.props?.className || "text-gray-500"
                }`,
              })}
            </div>
          )}
        </div>
      </div>

      {isOpen &&
        !disabled &&
        typeof document !== "undefined" &&
        createPortal(
          <FixedSizeCalendar
            onChange={onChange}
            value={value}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            disabled={disabled}
            locale={locale}
            dateFormat={dateFormat}
            disablePast={disablePast}
            disableFuture={disableFuture}
            minDate={minDate}
            maxDate={maxDate}
            setIsOpen={setIsOpen}
            setView={setView}
            setIsFocused={setIsFocused}
            setIsTyping={setIsTyping}
            setTypedValue={setTypedValue}
            inputRef={inputRef}
            position={position}
            portalRef={portalRef}
            yearPage={yearPage}
            setYearPage={setYearPage}
            view={view}
          />,
          document.body
        )}
    </div>
  );
};

const CommonDatePicker = ({
  name,
  control,
  label,
  disabled = false,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  placeholder = "",
  error,
  className = "",
  fontSize = "text-xs",
  inputSize = "h-8",
  inputBgColor = "bg-gray-50",
  isFloatingLabel = true,
  leftIcon,
  rightIcon,
  iconBgColor = "bg-gray-100",
  onLeftIconClick,
  onRightIconClick,
  defaultValue = "",
  locale = "en-GB",
  dateFormat = "DD-MM-YYYY",
  onClear,
  allowManualInput = true,
  onManualInputError,
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field: { onChange, value }, fieldState }) => (
        <DatePickerInner
          name={name}
          onChange={onChange}
          value={value}
          fieldState={fieldState}
          disabled={disabled}
          disablePast={disablePast}
          disableFuture={disableFuture}
          minDate={minDate}
          maxDate={maxDate}
          placeholder={placeholder}
          error={error}
          className={className}
          fontSize={fontSize}
          inputSize={inputSize}
          inputBgColor={inputBgColor}
          isFloatingLabel={isFloatingLabel}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          iconBgColor={iconBgColor}
          onLeftIconClick={onLeftIconClick}
          onRightIconClick={onRightIconClick}
          locale={locale}
          dateFormat={dateFormat}
          onClear={onClear}
          allowManualInput={allowManualInput}
          onManualInputError={onManualInputError}
          defaultValue={defaultValue}
          label={label}
        />
      )}
    />
  );
};

CommonDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.object,
  className: PropTypes.string,
  fontSize: PropTypes.oneOf(["text-xs", "text-sm", "text-base"]),
  inputSize: PropTypes.oneOf(["h-7", "h-8", "h-9", "h-10"]),
  inputBgColor: PropTypes.string,
  isFloatingLabel: PropTypes.bool,
  leftIcon: PropTypes.element,
  rightIcon: PropTypes.element,
  iconBgColor: PropTypes.string,
  onLeftIconClick: PropTypes.func,
  onRightIconClick: PropTypes.func,
  defaultValue: PropTypes.string,
  locale: PropTypes.string,
  dateFormat: PropTypes.string,
  onClear: PropTypes.func,
  allowManualInput: PropTypes.bool,
  onManualInputError: PropTypes.func,
};

CommonDatePicker.defaultProps = {
  disabled: false,
  disablePast: false,
  disableFuture: false,
  minDate: null,
  maxDate: null,
  placeholder: "",
  className: "",
  fontSize: "text-xs",
  inputSize: "h-8",
  inputBgColor: "bg-gray-50",
  isFloatingLabel: true,
  iconBgColor: "bg-gray-100",
  defaultValue: "",
  locale: "en-GB",
  dateFormat: "DD-MM-YYYY",
  allowManualInput: true,
  onManualInputError: null,
};

export default CommonDatePicker;
